import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layers, Palette } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { toast } from "sonner";
import { PageTitle } from "../components/site/PageTitle";
import { useCart } from "../context/CartContext";
import { ITEM_TYPES, PLACEMENTS } from "../lib/constants";

const empty = { item_type: "", colors: "", placement: "", quantity: 1, sizes: "", notes: "" };
const STEPS = [
  ["1. Submit details", "Choose your item type, colors, quantities, sizes, and upload your logo or design."],
  ["2. We review it", "ASAP & Co. checks the order details, artwork, placement, and pricing."],
  ["3. Invoice", "You'll receive an invoice or payment link before production begins."],
];

export default function Order() {
  const { add, items } = useCart();
  const { settings: s = {} } = useSite();
  const itemTypes = s.item_types?.length ? s.item_types : ITEM_TYPES;
  const placements = s.placements?.length ? s.placements : PLACEMENTS;
  const steps = s.home_steps?.length ? s.home_steps.map((x, i) => [`${i + 1}. ${x.title}`, x.text]) : STEPS;
  const nav = useNavigate();
  const [f, setF] = useState(empty);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = (goCheckout) => (e) => {
    e?.preventDefault();
    const form = document.getElementById("order-form");
    if (!form.reportValidity()) return;
    add({ ...f, quantity: Math.max(1, parseInt(f.quantity, 10) || 1) });
    toast.success(`${f.item_type} added to cart`);
    setF(empty);
    if (goCheckout) nav("/checkout");
  };

  return (
    <div className="pb-20">
      <PageTitle badge="Start your order" title="Custom Order" subtitle="Tell us what you need, upload your artwork at checkout, and ASAP & Co. will review your order and send an invoice." testId="order-title" />
      <div className="mx-auto grid max-w-6xl gap-8 px-6 lg:grid-cols-[1fr_320px]">
        <form id="order-form" onSubmit={submit(true)} className="panel space-y-5 p-6 sm:p-8" data-testid="order-form">
          <Link to="/design" className="flex items-center gap-3 rounded-xl border border-[#E5202A]/50 bg-[#E5202A]/10 px-4 py-3 text-sm transition-colors hover:bg-[#E5202A]/20" data-testid="order-design-studio-link">
            <Palette className="h-6 w-6 shrink-0 text-[#E5202A]" /><span><b className="font-display text-lg uppercase">Want to see it first?</b> Open the Design Studio to preview your logo on a shirt, hoodie, hat or mug.</span>
          </Link>
          {s.gang_enabled !== false && (
            <Link to="/gang-sheet" className="flex items-center gap-3 rounded-xl border border-[#2563EB]/50 bg-[#2563EB]/10 px-4 py-3 text-sm transition-colors hover:bg-[#2563EB]/20" data-testid="order-gang-sheet-link">
              <Layers className="h-6 w-6 shrink-0 text-[#60A5FA]" /><span><b className="font-display text-lg uppercase">Need DTF transfers?</b> Build a gang sheet with {s.gang_min_images || 6}+ pictures.</span>
            </Link>
          )}
          <h2 className="red-title text-2xl">Order Details</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Item Type *</label>
              <select className="field" required value={f.item_type} onChange={set("item_type")} data-testid="order-item-type-select">
                <option value="">Select item</option>
                {itemTypes.map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Item Color(s)</label>
              <input className="field" value={f.colors} onChange={set("colors")} placeholder="e.g. Black, Red" data-testid="order-colors-input" />
            </div>
            <div>
              <label className="label">Print Placement</label>
              <select className="field" value={f.placement} onChange={set("placement")} data-testid="order-placement-select">
                <option value="">Select placement</option>
                {placements.map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Quantity *</label>
              <input className="field" type="number" min="1" required value={f.quantity} onChange={set("quantity")} data-testid="order-quantity-input" />
            </div>
          </div>
          <div>
            <label className="label">Sizes / Breakdown</label>
            <input className="field" value={f.sizes} onChange={set("sizes")} placeholder="e.g. 4 S, 6 M, 10 L, 4 XL" data-testid="order-sizes-input" />
          </div>
          <div>
            <label className="label">Order Notes</label>
            <textarea className="field min-h-[110px]" value={f.notes} onChange={set("notes")} placeholder="Garment brand / style number, special instructions..." data-testid="order-notes-input" />
          </div>
          <p className="rounded-lg border border-[#2563EB]/40 bg-[#2563EB]/10 px-4 py-3 text-sm text-slate-300">You'll upload your final logo/design file on the checkout page after reviewing your cart.</p>
          <div className="flex flex-wrap gap-3">
            <button type="submit" className="btn-red" data-testid="order-add-and-checkout-button">Add to Cart & Checkout</button>
            <button type="button" onClick={submit(false)} className="btn-ghost" data-testid="order-add-another-button">Add & Add Another Item</button>
          </div>
          {items.length > 0 && <p className="text-sm text-slate-400" data-testid="order-cart-summary">{items.length} item(s) in your cart.</p>}
        </form>
        <aside className="panel h-fit p-6">
          <h3 className="red-title text-xl">How it works</h3>
          <ol className="mt-4 space-y-4">
            {steps.map(([t, d]) => <li key={t}><p className="font-display text-lg font-bold uppercase">{t}</p><p className="text-sm text-slate-400">{d}</p></li>)}
          </ol>
        </aside>
      </div>
    </div>
  );
}
